import { ExportOptions, ExportMode } from '../../types/models';
import {
	DocxProfileSelector,
	ExportFormatSelector,
	ExportThemeSelector,
} from '../settings/ExportSettings';
import { useChatManagementStore } from '../../stores/chatManagementStore';
import { useBackendOptions } from '../../stores/settingsRegistry';
import { usePlatformStore } from '../../stores/platformStore';

function ExportButton({
	selectedCount,
	isExporting,
	exportMode,
	onClick,
}: {
	selectedCount: number;
	isExporting: boolean;
	exportMode: ExportMode;
	onClick: () => void;
}) {
	const isDisabled = selectedCount === 0 || isExporting;
	const isConfirmMode = exportMode !== null;

	return (
		<button
			onClick={onClick}
			disabled={isDisabled}
			className={`px-6 py-2 rounded-small text-sm font-medium transition-colors duration-200 ${
				isDisabled
					? 'bg-neutral-500 text-neutral-300 cursor-not-allowed'
					: 'bg-red-600 hover:bg-red-500 text-neutral-100'
			}`}
			aria-label={
				isConfirmMode
					? 'Confirm and execute export'
					: 'Show export options'
			}
			title={
				isConfirmMode
					? 'Click again to confirm export'
					: 'Show export options'
			}
			type="button"
		>
			{isConfirmMode ? (
				<i className="fas fa-check mr-xs" />
			) : (
				<i className="fas fa-download mr-xs" />
			)}
			Export ({selectedCount})
		</button>
	);
}

function DeleteButton({
	selectedCount,
	onClick,
}: {
	selectedCount: number;
	onClick: () => void;
}) {
	const isDisabled = selectedCount === 0;

	return (
		<button
			onClick={onClick}
			disabled={isDisabled}
			className={`px-6 py-2 rounded-small text-sm font-medium transition-colors duration-200 ${
				isDisabled
					? 'bg-neutral-500 text-neutral-300 cursor-not-allowed'
					: 'bg-red-600 hover:bg-red-500 text-neutral-100'
			}`}
			aria-label="Delete selected chats"
			title="Delete selected chats"
			type="button"
		>
			<i className="fas fa-trash mr-xs" />
			Delete ({selectedCount})
		</button>
	);
}

interface TopBarProps {
	selectedCount: number;
	totalCount: number;
	options: ExportOptions;
	isExporting: boolean;
	exportMode: ExportMode;
	onSelectAll: () => void;
	onOptionsChange: (options: ExportOptions) => void;
	onExport: () => void;
	onDelete: () => void;
}

export function TopBar({
	selectedCount,
	totalCount,
	options,
	isExporting,
	exportMode,
	onSelectAll,
	onOptionsChange,
	onExport,
	onDelete,
}: TopBarProps) {
	const hasSelection = selectedCount > 0;
	const isEmpty = totalCount === 0;
	const isMagazine = options.format === 'magazine';
	const isWeb = !usePlatformStore.getState().isDesktop();
	const { actions } = useChatManagementStore();
	const encryptExport = useChatManagementStore((s) => s.export.encryptExport);
	const signExport = useChatManagementStore((s) => s.export.signExport);
	const backendOptions = useBackendOptions();
	const hasEncryptKey = backendOptions
		? Boolean(backendOptions.helpers.getValue('mag_encrypt_key'))
		: false;
	const hasSigningKey = backendOptions
		? Boolean(backendOptions.helpers.getValue('mag_signing_key'))
		: false;

	const magazineTooltip = (() => {
		const base = '.mag - portable archive for documents and chats';
		if (!backendOptions || (hasEncryptKey && hasSigningKey)) return base;
		let capability = 'encryption and signing';
		if (hasSigningKey) capability = 'encryption';
		else if (hasEncryptKey) capability = 'signing';
		return `${base}. Configure ${capability} in settings`;
	})();

	return (
		<div>
			<div className="mb-4 bg-neutral-600 rounded-medium p-sm border border-neutral-400">
				{/* First row: main controls */}
				<div className="flex flex-wrap items-center justify-between gap-md [&>*:nth-child(n+3)]:justify-end">
					<div className="flex items-center gap-md">
						<button
							onClick={onSelectAll}
							className="btn-secondary"
							disabled={isEmpty}
							aria-label={
								hasSelection
									? 'Deselect all chats'
									: 'Select all chats'
							}
							title={
								hasSelection
									? 'Deselect all chats'
									: 'Select all chats'
							}
							type="button"
						>
							{hasSelection ? 'Deselect All' : 'Select All'}
						</button>
						<span className="text-xs text-neutral-300 font-medium">
							{selectedCount} of {totalCount} selected
						</span>
					</div>

					<div className="flex items-center gap-sm">
						{isMagazine && (
							<i
								className="fas fa-info-circle text-neutral-400 text-sm cursor-help"
								title={magazineTooltip}
							/>
						)}
						{isWeb && options.format === 'pdf' && selectedCount > 1 && (
							<i
								className="fas fa-info-circle text-neutral-400 text-sm cursor-help"
								title="Bulk PDF exports are saved as print-ready HTML files. Open in a browser and print to PDF for full fidelity."
							/>
						)}
						<ExportFormatSelector
							value={options.format}
							onChange={(format) =>
								onOptionsChange({ ...options, format })
							}
						/>
						{!isMagazine && options.format === 'pdf' && (
							<ExportThemeSelector
								value={options.theme}
								onChange={(theme) =>
									onOptionsChange({ ...options, theme })
								}
							/>
						)}
						{!isMagazine && options.format === 'docx' && (
							<DocxProfileSelector
								value={options.config}
								onChange={(config) =>
									onOptionsChange({ ...options, config })
								}
							/>
						)}
						{isMagazine && hasEncryptKey && (
							/* eslint-disable-next-line jsx-a11y/label-has-associated-control */
							<label className="flex items-center gap-xs text-xs text-neutral-200 cursor-pointer">
								<input
									type="checkbox"
									checked={encryptExport}
									onChange={(e) => actions.setEncryptExport(e.target.checked)}
									className="accent-red-500"
								/>
								Encrypt
							</label>
						)}
						{isMagazine && hasSigningKey && (
							/* eslint-disable-next-line jsx-a11y/label-has-associated-control */
							<label className="flex items-center gap-xs text-xs text-neutral-200 cursor-pointer">
								<input
									type="checkbox"
									checked={signExport}
									onChange={(e) => actions.setSignExport(e.target.checked)}
									className="accent-red-500"
								/>
								Sign
							</label>
						)}
						<ExportButton
							selectedCount={selectedCount}
							isExporting={isExporting}
							exportMode={exportMode}
							onClick={onExport}
						/>
					</div>
					<div className="flex items-center gap-sm">
						<DeleteButton
							selectedCount={selectedCount}
							onClick={onDelete}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
